/* eslint-disable @typescript-eslint/no-explicit-any */

import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import merge from 'lodash/merge';
import { DunningLevel, ScoringRiskType } from '@aston/foundation';
import { BehaviorSubject } from 'rxjs';

import { BusinessThemeService, ITheme } from '../models';

export const [ light, dark, medium ] = [ '#FFFFFF', '#333333', '#616161' ]
export interface BusinessColor { background: string; foreground: string; }
export const compose = (background: string, foreground: string): BusinessColor => ({ background, foreground })


@Injectable({
	providedIn: 'root'
})
export class DefaultBusinessThemeService implements BusinessThemeService {

	protected themes: ITheme[] = []
	protected current$ = new BehaviorSubject({})

	private dunningLevelsColors = {
		[DunningLevel.STD]: compose('#155aa7', light),
		[DunningLevel.PAA]: compose('#6ed07a', dark),
		[DunningLevel.PPA]: compose('#158e42', dark),
		[DunningLevel.LTG]: compose('#ffb95b', dark),
		[DunningLevel.CTX]: compose('#f04c2a', dark),
		[DunningLevel.EXR]: compose('#d2d2d2', dark),
	}

	private dunningActionTypeColors = {
		0: compose('#002855', light),
		1: compose('#44699C', light),
		2: compose('#34699D', light),
		3: compose('#4E82B0', dark),
		4: compose('#699BC2', dark),
		5: compose('#83B3D4', dark),
		6: compose('#9DCCE7', medium),
		7: compose('#B7E5F9', medium),
		8: compose('#D8F9FF', medium),
	}

	private kpisColors = {
		0: compose('#1558A6', light),
		1: compose('#2C6BB0', light),
		2: compose('#447DBA', dark),
		3: compose('#5B90C3', dark),
		4: compose('#73A2CD', dark),
		5: compose('#8AB5D7', dark),
		6: compose('#A2C7E1', dark),
		7: compose('#B9DAEA', dark),
		8: compose('#D1ECF4', dark),
		9: compose('#E8FFFE', dark),
		danger: compose('#f3745a', light)
	}

	private ageingBalanceColors = {
		positives: ['#1EAF53', '#F4E544'],
		negatives: ['#F4E544', '#FF4723'],
	}

	private scoringColors = {
		[ScoringRiskType.Low]: compose('#2372f6', dark),
		[ScoringRiskType.Moderate]: compose('#FDC423', dark),
		[ScoringRiskType.Strong]: compose('#F96F24', dark),
		[ScoringRiskType.High]: compose('#f42626', dark),
	}

	private delayDaysColors = {
		5: '#1EAF53',
		10: '#2372f6',
		20: '#EBEB26', 
		30: '#ffab12',
		31: '#f42626',
	}

	private disputeColors = {
		severity: ['#F96F24', '#F4E544'],
	}

	private litigationColors = {
		severity: ['#f42626', '#F96F24'],
	}

	private turnoverLineColors = {
		turnover: compose('#FFCC00', dark),
		medianTurnover: compose('#28D697', dark),
		avgTurnover: compose('#2372f6', dark),
	}

	private aiChat = {
		intro: {
			color: '#000',
		},
		messages: {
			shared: {
				bubble: {
					color: '#fff'
				}
			},
			ai: {
				bubble: {
					color: '#000'
				}
			},
		},
		textInput: {
			text: {
				color: "black",
				padding: "0.5rem"
			},
			container: {
				minHeight: "50px",
				maxHeight: "100px",
				'padding-bottom': "1rem"
			},
		}
	}

	getThemed(key: keyof DefaultBusinessThemeService) {
		if (this[key]) return this.current$.pipe(
			map((theme: any) => merge({}, this[key], theme[key]))
		);
		throw new Error(`unknown theme part: ${key}`)
	}

	install(...themes: ITheme[]) {
		this.themes = this.themes.concat(themes)
	}

	useTheme(themeId: string) {
		const t: any = this.themes.find(t => t.id === themeId);
		this.current$.next(t.business || {})
	}
}
